import { InfoMessage } from '@/components/shared/InfoMessage/InfoMessage';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/Accordion/Accordion';
import { Button } from '@/components/ui/Button/Button';
import { Calendar } from '@/components/ui/Calendar/Calendar';
import { Separator } from '@/components/ui/Separator/Separator';
import { Spinner } from '@/components/ui/Spinner/Spinner';
import { toast } from '@/components/ui/Toast/Toast';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/ToggleGroup/ToggleGroup';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/Tooltip/Tooltip';
import { paths } from '@/config/paths';
import { useTimeFor } from '@/hooks/useTimeFor';
import type { Student } from '@/types/student';
import { cn } from '@/utils';
import { capitalize } from '@/utils/capitalize';
import { toUtcIsoString } from '@/utils/timezone';
import { addMinutes, endOfMonth, format, isEqual, isSameDay, isSameMonth, startOfDay, startOfMonth } from 'date-fns';
import { ChevronRight, HelpCircle, TriangleAlert } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useGetCalendarPeriod } from '../../api/getCalendarPeriod';
import type { StudentSchedulingDetails } from '../../api/getStudentsSchedulingDetails';
import { useGetTimeslotsPerDay } from '../../api/getTimeslotsPerDay';
import { getMaxCalendarDate } from '../../utils/getMaxCalendarDate';
import type { StudentsSessionDetails } from '../ScheduleDialog/context/ScheduleFlowContext';
import { getInitialDisplayMonth } from './utils/getInitialDisplayMonth';
import { getStudentsSessionsData } from './utils/getStudentsSessionsData';

export const CALENDAR_DEFAULT_NAV_BUTTON_CLASSNAMES =
  'hover:opacity-80 p-2.5 aria-disabled:[&>svg]:text-text-default-secondary aria-disabled:pointer-events-none z-10';

interface SchedulingCalendarProps extends Omit<React.ComponentProps<typeof Calendar>, 'value' | 'onChange'> {
  value?: string;
  onChange?: (value: string) => void;
  students: Student[];
  onDialogClose?: () => void;
  studentSessionDetails: StudentsSessionDetails[];
  studentsConfig: StudentSchedulingDetails[];
  reschedulingAppointmentId?: string;
  isRecurring?: boolean;
}

export const SchedulingCalendar = ({
  value,
  onChange,
  students,
  onDialogClose,
  studentSessionDetails,
  studentsConfig,
  reschedulingAppointmentId,
  isRecurring = false,
  ...props
}: SchedulingCalendarProps) => {
  // We need to control accordion state in parent to prevent calendar lib from collapsing it on date change

  const [accordionState, setIsAccordionState] = useState('content');
  const [appointmentsAccordionState, setAppointmentsAccordionState] = useState('content');

  const { t } = useTranslation('scheduling', { keyPrefix: 'schedulingCalendar' });

  const navigate = useNavigate();

  /**
   * MYM-260: every day cell, timeslot and cut-off below is read through the center's clock, so a
   * guardian in another zone books the slot the center actually holds open. The same zone is sent
   * to the backend, which groups days by it.
   */
  const { timeZone: centerTimeZone, zoned, now } = useTimeFor('sessionTime');

  const maxSingleDates = studentSessionDetails.map((student) =>
    getMaxCalendarDate(student.enrollmentEndDate, student.centerDetails.max_advance_booking_single, centerTimeZone),
  );

  const maxRecurringDates = studentSessionDetails.map((student) =>
    getMaxCalendarDate(student.enrollmentEndDate, student.centerDetails.max_advance_booking_recurring, centerTimeZone),
  );

  const maxSingleDate: Date | undefined = maxSingleDates.length > 0 ? zoned(Math.min(...maxSingleDates.map((d) => d.getTime()))) : undefined;
  const maxRecurringDate: Date | undefined = maxRecurringDates.length > 0 ? zoned(Math.min(...maxRecurringDates.map((d) => d.getTime()))) : undefined;

  const maxDate = isRecurring ? maxRecurringDate : maxSingleDate;

  const [displayMonth, setDisplayMonth] = useState<Date>(() => {
    if (value) return zoned(value);

    const today = now();
    // MYM-398: a pre-enrolled student has no bookable day in the current month, so open on the
    // enrollment's start month. Not past maxDate, where every day is disabled anyway.
    const firstSchedulableMonth = getInitialDisplayMonth(students, studentsConfig ?? [], studentSessionDetails ?? [], today, centerTimeZone);

    if (!firstSchedulableMonth || (maxDate && firstSchedulableMonth > maxDate)) return today;

    return firstSchedulableMonth;
  });

  const [selectedDay, setSelectedDay] = useState<Date | undefined>(() => (value ? zoned(value) : undefined));

  const lastValidSelection = useRef<{ day?: Date; value?: string }>({
    day: value ? zoned(value) : undefined,
    value,
  });

  useEffect(() => {
    if (value) {
      lastValidSelection.current = {
        day: zoned(value),
        value,
      };
    }
  }, [value, zoned]);

  const selectedTimeslot = useMemo(() => (value ? zoned(value) : undefined), [value, zoned]);

  const studentsSessionDetails = useMemo(
    () =>
      studentSessionDetails.map((detail) => ({
        studentId: detail.studentId,
        enrollmentId: detail.enrollmentId,
        sessionType: detail.sessionType,
        centerId: detail.centerDetails.center_id,
        deliveryType: detail.deliveryType,
        duration: detail.durationInMinutes,
      })),
    [studentSessionDetails],
  );

  const {
    data: period,
    isFetching: isPeriodFetching,
    error: periodError,
  } = useGetCalendarPeriod({
    params: {
      students: studentsSessionDetails,
      startDate: toUtcIsoString(startOfMonth(displayMonth)),
      endDate: toUtcIsoString(endOfMonth(displayMonth)),
      reschedulingAppointmentId: reschedulingAppointmentId,
      timeZone: centerTimeZone,
    },
    options: {
      enabled: studentSessionDetails.length > 0,
    },
  });

  const {
    data: timeslots,
    isFetching: isTimeslotsFetching,
    error: timeslotsError,
  } = useGetTimeslotsPerDay({
    params: {
      students: studentsSessionDetails,
      date: selectedDay ? toUtcIsoString(startOfDay(selectedDay)) : '',
      reschedulingAppointmentId: reschedulingAppointmentId,
      timeZone: centerTimeZone,
    },
    options: {
      enabled: !!selectedDay && studentSessionDetails.length > 0,
    },
  });

  useEffect(() => {
    if (periodError) {
      toast({
        variant: 'destructive',
        description: t('errors.loadPeriodError'),
      });
    }

    if (timeslotsError) {
      toast({
        variant: 'destructive',
        description: t('errors.loadTimeslotsError'),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodError, timeslotsError]);

  const availableDaysWithSlots = useMemo(() => period?.available_days.filter((day) => day.can_book).map((day) => day.date) ?? [], [period?.available_days]);

  const isSelectedDayInDisplayMonth = useMemo(() => {
    if (!selectedDay) return false;
    return selectedDay.getMonth() === displayMonth.getMonth() && selectedDay.getFullYear() === displayMonth.getFullYear();
  }, [selectedDay, displayMonth]);

  const availableTimeSlotsForSelectedDate = useMemo(() => {
    if (!selectedDay || !timeslots?.timeslots || timeslots.timeslots.length === 0) return [];

    const currentTime = now();
    const today = startOfDay(currentTime);

    return timeslots.timeslots.filter((timeslot) => {
      const slotStartTime = zoned(timeslot.date);

      const isInDisplayMonth = slotStartTime.getMonth() === displayMonth.getMonth() && slotStartTime.getFullYear() === displayMonth.getFullYear();

      if (!isInDisplayMonth) {
        return false;
      }

      if (isSameDay(selectedDay, today)) {
        return slotStartTime > currentTime;
      }

      return true;
    });
  }, [timeslots, selectedDay, displayMonth, now, zoned]);

  const hasUserSelectedDayRef = useRef(!!value);

  useEffect(() => {
    if (hasUserSelectedDayRef.current) return;

    if (availableDaysWithSlots && availableDaysWithSlots.length > 0) {
      const firstAvailableDay = availableDaysWithSlots.find((dayString) => !isDayDisabled(zoned(dayString)));

      if (firstAvailableDay) {
        hasUserSelectedDayRef.current = true;
        setSelectedDay(zoned(firstAvailableDay));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableDaysWithSlots]);

  // Automatically select first available timeslot when day or timeslots change
  useEffect(() => {
    const firstSlot = availableTimeSlotsForSelectedDate[0];

    const isValueAlreadySetForThisDay =
      value &&
      availableTimeSlotsForSelectedDate.some((slot) => {
        return isEqual(new Date(slot.date), new Date(value));
      });

    if (selectedDay && firstSlot && !isValueAlreadySetForThisDay) {
      const firstSlotDate = zoned(firstSlot.date);
      const belongsToSelectedDay = isSameDay(firstSlotDate, selectedDay);

      if (belongsToSelectedDay) {
        onChange?.(firstSlot.date);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableTimeSlotsForSelectedDate, isTimeslotsFetching, value, selectedDay]);

  const handleDateSelect = (day: Date | undefined) => {
    if (!day || (selectedDay && isSameDay(day, selectedDay))) return;
    onChange?.('');
    setSelectedDay(day);
  };

  const isDayDisabled = (day: Date) => {
    const today = startOfDay(now());
    const dayToCheck = startOfDay(zoned(day));

    const hasSlots = availableDaysWithSlots.some((availableDay) => isSameDay(dayToCheck, availableDay));

    return dayToCheck < today || !hasSlots || (maxDate ? dayToCheck > maxDate : false);
  };

  const monthName = format(displayMonth, 'LLLL');
  const month = monthName ? capitalize(monthName) : '';

  const studentsText =
    students && students.length > 1
      ? students
          .map((s) => s.firstName)
          // eslint-disable-next-line unicorn/no-array-reduce
          .reduce((acc, curr, idx, arr) => {
            if (idx === 0) return curr;
            if (idx === arr.length - 1) return `${acc} ${t('multipleStudentsNotice.and')} ${curr}`;
            return `${acc}, ${curr}`;
          }, '')
      : undefined;

  const shouldHidePreviousMonthButton = displayMonth ? displayMonth.getMonth() <= now().getMonth() && displayMonth.getFullYear() <= now().getFullYear() : false;

  const studentsSessionsData = getStudentsSessionsData(students, studentsConfig ?? [], studentSessionDetails ?? [], displayMonth, centerTimeZone);

  const isAnyStudentWithoutSessions = useMemo(() => {
    if (reschedulingAppointmentId) return false;
    return Object.values(studentsSessionsData).some((data) => data.remainingSessions <= 0);
  }, [studentsSessionsData, reschedulingAppointmentId]);

  const onViewYourSessions = () => {
    void navigate(paths.scheduling.sessions.home.path);
    onDialogClose?.();
  };

  const bookedDays = period?.appointments?.map((appointment) => zoned(appointment.appointment_date)) ?? [];

  const appointmentsForSelectedDay = selectedDay
    ? (period?.appointments?.filter((appointment) => isSameDay(zoned(appointment.appointment_date), selectedDay)) ?? [])
    : [];

  return (
    <>
      <Calendar
        {...props}
        mode='single'
        timeZone={centerTimeZone}
        month={displayMonth}
        bookedDays={bookedDays}
        onMonthChange={(newMonth) => {
          setDisplayMonth(newMonth);

          const saved = lastValidSelection.current;
          if (saved.day && isSameMonth(saved.day, newMonth)) {
            setSelectedDay(saved.day);
            if (saved.value) onChange?.(saved.value);
          } else {
            setSelectedDay(undefined);
            onChange?.('');
          }
        }}
        endMonth={maxDate}
        disabled={isAnyStudentWithoutSessions || isDayDisabled}
        selected={isAnyStudentWithoutSessions ? undefined : (selectedDay ?? selectedTimeslot)}
        onSelect={handleDateSelect}
        classNames={{
          button_previous: cn(CALENDAR_DEFAULT_NAV_BUTTON_CLASSNAMES, {
            invisible: shouldHidePreviousMonthButton,
          }),
        }}
        isLoading={isPeriodFetching}
        showOutsideDays={false}
        contentBelowNavigation={
          <>
            <Accordion type='single' collapsible className='w-full' value={accordionState} onValueChange={(value) => setIsAccordionState(value)}>
              <AccordionItem value='content' className='bg-fill-background-light text-body-m-regular w-full items-center rounded-lg px-5'>
                <AccordionTrigger className={cn({ 'text-text-brand-primary': isAnyStudentWithoutSessions })}>
                  {isAnyStudentWithoutSessions ? (
                    <div className='flex items-center gap-2'>
                      <TriangleAlert size={20} />
                      {t('remainingSessions.noSessionsTitle')}
                    </div>
                  ) : (
                    t('remainingSessions.title', { month })
                  )}
                </AccordionTrigger>
                <AccordionContent>
                  <ul className='space-y-3 py-2'>
                    {students.map((student) => {
                      const studentData = studentsSessionsData[student.id];
                      const noSessionsLeft = studentData.remainingSessions <= 0;

                      return (
                        <li
                          key={student.id}
                          className={cn('flex justify-between truncate', {
                            'text-text-brand-primary': noSessionsLeft,
                          })}
                        >
                          <p className='text-body-m-semibold truncate'>
                            {student.firstName} {student.lastName}
                          </p>
                          <p className='flex-shrink-0'>
                            {studentData.remainingSessions === Infinity
                              ? t('remainingSessions.flexible')
                              : t('remainingSessions.counter', { count: studentData.remainingSessions })}
                          </p>
                        </li>
                      );
                    })}
                  </ul>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
            {isAnyStudentWithoutSessions && (
              <InfoMessage className='mt-4' variant='info' title={t('bookingAdvice.title')} description={t('bookingAdvice.description')}>
                <Separator className='mt-2' />
                <Button variant='text-secondary' onClick={onViewYourSessions}>
                  {t('bookingAdvice.viewYourSessions')} <ChevronRight strokeWidth={1.5} />
                </Button>
              </InfoMessage>
            )}
          </>
        }
      />
      <>
        {(appointmentsForSelectedDay.length > 0 || students.length > 1) && (
          <div className='mt-6 space-y-3'>
            {appointmentsForSelectedDay.length > 0 && !isPeriodFetching && (
              <Accordion
                type='single'
                collapsible
                className='w-full'
                value={appointmentsAccordionState}
                onValueChange={(value) => setAppointmentsAccordionState(value)}
              >
                <AccordionItem value='content' className='bg-fill-info-tile-blue text-body-m-regular w-full items-center rounded-lg px-5'>
                  <AccordionTrigger>{t('sessionsForTheDay.title')}</AccordionTrigger>
                  <AccordionContent>
                    <ul className='space-y-3 py-2'>
                      {appointmentsForSelectedDay.map((appointment) => {
                        const student = students.find((s) => s.id === appointment.student_id);
                        if (!student) return null;

                        return (
                          <li key={appointment.appointment_id} className='flex truncate'>
                            <div className='text-text-default-secondary'>•</div>
                            <div className='ml-3'>
                              <p className='text-body-m-semibold truncate'>
                                {student.firstName} {student.lastName}
                              </p>
                              <div className='text-text-default-secondary flex items-center'>
                                {format(zoned(appointment.appointment_date), 'p')} -{' '}
                                {format(addMinutes(zoned(appointment.appointment_date), appointment.duration_minutes), 'p')}
                                {appointment.session_type_enum === 'private' && (
                                  <>
                                    <div className='bg-text-default-secondary i mx-1.5 size-[3px] rounded-full' />
                                    <div>{t('sessionsForTheDay.privateSession')}</div>
                                  </>
                                )}
                              </div>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            )}
            {students && students.length > 1 && (
              <div className='bg-fill-background-light flex w-full items-center justify-between rounded-lg px-5 py-4'>
                <p>{t('multipleStudentsNotice.description', { students: studentsText })}</p>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button type='button' variant='icon' size='icon-l' aria-label={t('multipleStudentsNotice.ariaLabel')}>
                        <HelpCircle className='text-text-default-secondary size-6' />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent showArrow side='bottom' align='end'>
                      <p>{t('multipleStudentsNotice.tooltip')}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            )}
          </div>
        )}
        {isPeriodFetching ? null : (
          <>
            {isTimeslotsFetching && (
              <div className='mt-6 flex w-full justify-center'>
                <Spinner className='size-14 stroke-1' />
              </div>
            )}
            {!isTimeslotsFetching && (
              <div className='mt-6'>
                {isSelectedDayInDisplayMonth && availableTimeSlotsForSelectedDate.length > 0 && (
                  <>
                    <p className='text-body-l-semibold mb-4'>{t('availableTimeSlots.title')}</p>
                    <ToggleGroup
                      type='single'
                      className='grid grid-cols-3 gap-3'
                      value={value}
                      onValueChange={(value) => {
                        if (value) {
                          onChange?.(value);
                        }
                      }}
                    >
                      {availableTimeSlotsForSelectedDate.map((timeslot) => (
                        <ToggleGroupItem
                          key={timeslot.date}
                          value={timeslot.date}
                          aria-label={t('availableTimeSlots.ariaLabel')}
                          className='text-body-m-regular border-border-default-primary'
                        >
                          <time dateTime={timeslot.date}>{format(zoned(timeslot.date), 'p')}</time>
                        </ToggleGroupItem>
                      ))}
                    </ToggleGroup>
                  </>
                )}

                {isSelectedDayInDisplayMonth && availableTimeSlotsForSelectedDate.length === 0 && (
                  <p className='text-body-m-regular text-text-default-secondary'>{t('availableTimeSlots.noSlots')}</p>
                )}

                {(!selectedDay || !isSelectedDayInDisplayMonth) && (
                  <p className='text-body-m-regular text-text-default-secondary'>{t('availableTimeSlots.empty')}</p>
                )}
              </div>
            )}
          </>
        )}
      </>
    </>
  );
};
